import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import {
    DetailedDraftOperationT,
    ResourceBudgetProposalAssociationT,
} from 'routes/BudgetModule/config/types';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';

import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import { useGetBudgetProposals } from 'routes/BudgetModule/helpers/hooks/budgetProposals';
import Flex from 'ui/Flex/Flex';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { DraftOperationNameEditModal } from './DraftOperationNameEditModal';
import {
    useDeleteDraftOperationMutation,
    useEditDraftOperationMutation,
} from '../hooks';
import { DraftOperationAssociationsEditModal } from './DraftOperationAssociationsEditModal';
import { mergeAssociationData } from '../helpers/dataHelpers';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

export const DraftOperationActionsCellRenderer: React.FC<{
    data: DetailedDraftOperationT;
    exercice: number;
}> = ({ data, exercice }) => {
    const { t } = useTranslation();

    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showEditNameModal, setShowEditNameModal] = useState(false);
    const [showEditAssociationsModal, setShowEditAssociationsModal] =
        useState(false);

    const [name, setName] = useState(data.name);

    const [anchorEl, setAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const { data: budgetProposals } = useGetBudgetProposals();

    const initialAssociations = mergeAssociationData({
        budgetProposals,
        associations: data.associations,
    });

    const [associations, setAssociations] =
        useState<ResourceBudgetProposalAssociationT[]>(initialAssociations);

    const deleteDraftOperationMutation = useDeleteDraftOperationMutation({
        draftOperationSoftwareId: data.softwareId,
    });

    const editDraftOperationMutation = useEditDraftOperationMutation({
        draftOperationSoftwareId: data.softwareId,
        name,
        associations,
        closeModal: () => {
            setShowEditNameModal(false);
            setShowEditAssociationsModal(false);
        },
    });

    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();

    return (
        <Flex alignItems="center">
            <IconButton
                size="small"
                onClick={(event) => {
                    setAnchorEl(event.currentTarget);
                }}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={!!anchorEl}
                onClose={closeMenu}
            >
                <MenuItem
                    onClick={() => {
                        trackEventBudget(
                            TRACKABLE_EVENTS.budget.budgetOverview.operations
                                .draftOperation.rename,
                            {
                                exercice,
                            },
                        );
                        closeMenu();
                        setShowEditNameModal(true);
                    }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('common.rename')} />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        trackEventBudget(
                            TRACKABLE_EVENTS.budget.budgetOverview.operations
                                .draftOperation.editAssociations,
                            {
                                exercice,
                            },
                        );
                        closeMenu();
                        setShowEditAssociationsModal(true);
                    }}
                >
                    <ListItemIcon>
                        <ShareIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary={t('createDraftOperation.manageResources')}
                    />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        trackEventBudget(
                            TRACKABLE_EVENTS.budget.budgetOverview.operations
                                .draftOperation.delete,
                            {
                                exercice,
                            },
                        );
                        closeMenu();
                        setShowConfirmDeleteModal(true);
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('common.delete')} />
                </MenuItem>
            </Menu>
            <ConfirmDeleteModal
                show={showConfirmDeleteModal}
                cancel={() => {
                    setShowConfirmDeleteModal(false);
                }}
                confirm={() => {
                    trackEventBudget(
                        TRACKABLE_EVENTS.budget.budgetOverview.operations
                            .draftOperation.confirmDeletion,
                        {
                            exercice,
                        },
                    );
                    deleteDraftOperationMutation.mutate();
                }}
                title={t('createDraftOperation.deleteConfirm', {
                    context: sourceTable,
                })}
            />
            <DraftOperationNameEditModal
                show={showEditNameModal}
                cancel={() => {
                    setShowEditNameModal(false);
                }}
                name={name}
                setName={setName}
                confirm={editDraftOperationMutation.mutate}
                initialName={data.name}
                exercice={exercice}
            />
            <DraftOperationAssociationsEditModal
                show={showEditAssociationsModal}
                cancel={() => {
                    setShowEditAssociationsModal(false);
                }}
                associations={associations}
                setAssociations={setAssociations}
                confirm={editDraftOperationMutation.mutate}
                exercice={exercice}
            />
        </Flex>
    );
};
